import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaReact, FaNodeJs, FaDatabase, FaGitAlt, FaServer,
  FaHtml5, FaCss3Alt, FaJs, FaDocker, FaAws, FaNpm,
  FaFigma, FaSass, FaBootstrap, FaChartBar, FaLayerGroup,
  FaSlack, FaJira, FaGithub, FaGitlab
} from 'react-icons/fa';
import { SiTypescript, SiMongodb, SiExpress, SiRedis, SiTailwindcss, SiNextdotjs, SiFirebase, SiSocketdotio, SiGraphql, SiPostman, SiSwagger, SiVisualstudiocode, SiClickup, SiRedux, SiWebpack, SiMysql, SiSequelize, SiGrafana } from 'react-icons/si';
import { useTranslation } from 'react-i18next';

const Skills: React.FC = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  const skillCategories = [
    {
      category: t('skills.frontend'),
      icon: <FaLayerGroup />,
      color: "from-primary-500 to-blue-500",
      description: t('skills.descriptions.frontend'),
      skills: [
        { name: 'React', icon: <FaReact />, color: 'text-primary-400', level: 95 },
        { name: 'React Native', icon: <FaReact />, color: 'text-primary-400', level: 85 },
        { name: 'Redux', icon: <SiRedux />, color: 'text-purple-500', level: 90 },
        { name: 'TypeScript', icon: <SiTypescript />, color: 'text-blue-500', level: 90 },
        { name: 'JavaScript', icon: <FaJs />, color: 'text-yellow-400', level: 95 },
        { name: 'HTML5', icon: <FaHtml5 />, color: 'text-red-500', level: 98 },
        { name: 'CSS3', icon: <FaCss3Alt />, color: 'text-blue-500', level: 95 },
        { name: 'Tailwind CSS', icon: <SiTailwindcss />, color: 'text-teal-400', level: 90 },
        { name: 'Next.js', icon: <SiNextdotjs />, color: 'text-gray-200', level: 85 },
        { name: 'Sass', icon: <FaSass />, color: 'text-pink-500', level: 85 },
        { name: 'Bootstrap', icon: <FaBootstrap />, color: 'text-purple-500', level: 90 },
        { name: 'Material-UI', icon: <FaServer />, color: 'text-blue-600', level: 85 },
        { name: 'Webpack', icon: <SiWebpack />, color: 'text-blue-400', level: 80 },
        { name: 'Figma', icon: <FaFigma />, color: 'text-pink-400', level: 80 },
      ]
    },
    {
      category: t('skills.backend'),
      icon: <FaServer />,
      color: "from-accent-500 to-teal-500",
      description: t('skills.descriptions.backend'),
      skills: [
        { name: 'Node.js', icon: <FaNodeJs />, color: 'text-green-500', level: 95 },
        { name: 'Express', icon: <SiExpress />, color: 'text-gray-300', level: 90 },
        { name: 'Socket.io', icon: <SiSocketdotio />, color: 'text-gray-200', level: 90 },
        { name: 'MongoDB', icon: <SiMongodb />, color: 'text-green-400', level: 92 },
        { name: 'Mongoose', icon: <FaServer />, color: 'text-green-500', level: 90 },
        { name: 'MySQL', icon: <SiMysql />, color: 'text-blue-500', level: 85 },
        { name: 'SQL', icon: <FaDatabase />, color: 'text-blue-300', level: 80 },
        { name: 'Sequelize', icon: <SiSequelize />, color: 'text-blue-400', level: 85 },
        { name: 'Redis', icon: <SiRedis />, color: 'text-red-500', level: 85 },
        { name: 'Firebase', icon: <SiFirebase />, color: 'text-yellow-500', level: 85 },
        { name: 'GraphQL', icon: <SiGraphql />, color: 'text-pink-500', level: 75 },
      ]
    },
    {
      category: t('skills.tools'),
      icon: <FaChartBar />,
      color: "from-purple-500 to-pink-500",
      description: t('skills.descriptions.tools'),
      skills: [
        { name: 'Git', icon: <FaGitAlt />, color: 'text-red-400', level: 90 },
        { name: 'GitHub', icon: <FaGithub />, color: 'text-gray-200', level: 95 },
        { name: 'GitLab', icon: <FaGitlab />, color: 'text-orange-500', level: 85 },
        { name: 'Docker', icon: <FaDocker />, color: 'text-blue-400', level: 85 },
        { name: 'AWS', icon: <FaAws />, color: 'text-yellow-500', level: 80 },
        { name: 'Grafana', icon: <SiGrafana />, color: 'text-orange-400', level: 75 },
        { name: 'NPM', icon: <FaNpm />, color: 'text-red-500', level: 95 },
        { name: 'Postman', icon: <SiPostman />, color: 'text-orange-500', level: 90 },
        { name: 'Swagger', icon: <SiSwagger />, color: 'text-green-500', level: 85 },
        { name: 'VS Code', icon: <SiVisualstudiocode />, color: 'text-blue-400', level: 95 },
        { name: 'Figma', icon: <FaFigma />, color: 'text-purple-500', level: 80 },
        { name: 'Jira', icon: <FaJira />, color: 'text-blue-600', level: 85 },
        { name: 'Slack', icon: <FaSlack />, color: 'text-purple-400', level: 90 },
        { name: 'ClickUp', icon: <SiClickup />, color: 'text-purple-500', level: 85 },
      ]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !hasAnimated) {
        setIsVisible(true);
        setHasAnimated(true);
      }
    }, { threshold: 0.1 });

    const section = document.getElementById('skills');
    if (section) observer.observe(section);

    return () => {
      if (section) observer.unobserve(section);
    };
  }, [hasAnimated]);

  return (
    <section
      id="skills"
      className="py-24 relative"
    >
      <div className="absolute top-1/4 left-0 w-1/3 h-1/3 bg-primary-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5 hidden md:block"></div>
      <div className="absolute bottom-1/4 right-0 w-1/3 h-1/3 bg-accent-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5 hidden md:block"></div>

      <div className="responsive-container">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            {t('skills.title')}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('skills.subtitle')}
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {skillCategories.map((category, index) => (
            <motion.button
              key={category.category}
              className={`px-6 py-3 rounded-full flex items-center gap-3 text-sm font-medium transition-all duration-300
                ${
                  activeCategory === index
                    ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-lg shadow-primary-500/25'
                    : 'bg-gray-800/50 backdrop-blur-sm text-gray-300 hover:text-white hover:bg-gray-700/50'
                }`}
              onClick={() => setActiveCategory(index)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className={`text-lg transition-colors duration-300 ${activeCategory === index ? 'text-white' : 'text-gray-400'}`}>
                {category.icon}
              </span>
              <span>{category.category}</span>
            </motion.button>
          ))}
        </div>

        <div
          className="text-center mb-12 max-w-3xl mx-auto"
          key={`desc-${activeCategory}`}
        >
          <p className="text-gray-300 text-lg">
            {skillCategories[activeCategory].description}
          </p>
        </div>

        <div className="mt-8">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4">
            {skillCategories[activeCategory].skills.map((skill, index) => (
              <div
                key={skill.name}
                className={`bg-gray-900 rounded-lg p-2 sm:p-3 border border-gray-800 text-center h-[80px] sm:h-[100px] flex flex-col justify-center
                  will-change-transform will-change-opacity
                  hover:border-primary-500 hover:shadow-[0_0_15px_rgba(99,102,241,0.3)]
                  transform-gpu transition-all duration-300 ease-out
                  group ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}
                style={{
                  transform: 'translate3d(0, 0, 0)',
                  backfaceVisibility: 'hidden',
                  animationDelay: `${0.1 + index * 0.05}s`,
                  animationFillMode: 'both',
                  animationDuration: '0.5s'
                }}
              >
                <div className="flex flex-col items-center justify-center">
                  <div
                    className={`text-lg sm:text-2xl ${skill.color} mb-1 transform-gpu transition-transform duration-300 ease-out group-hover:scale-110`}
                    style={{ transform: 'translate3d(0, 0, 0)' }}
                  >
                    {skill.icon}
                  </div>
                  <h4 className="text-xs font-medium text-white transition-colors duration-300 ease-out group-hover:text-primary-300">
                    {skill.name}
                  </h4>
                  <div className="h-0.5 w-0 bg-gradient-to-r from-primary-500 to-accent-500 mt-1 transform-gpu transition-all duration-300 ease-out group-hover:w-8"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="responsive-container mt-24 pt-16 border-t border-gray-800">
        <div className="text-center">
          <div className="glass rounded-xl p-4 sm:p-6 max-w-3xl mx-auto">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-4 text-white">{t('skills.continuousLearning.title')}</h3>
            <p className="text-gray-300 text-sm sm:text-base">
              {t('skills.continuousLearning.description')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;
